/**
 * Webtech HR Engine™ - Landing Page
 * Version 3.0 - Design Premium avec animations et conversions optimisées
 * Inspiré d'Odoo, Notion, Linear, Stripe
 */

import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  Network, 
  Users, 
  ChevronRight,
  Check,
  Globe,
  Shield,
  Zap,
  Star,
  Building2,
  Target,
  Brain,
  X,
  Phone,
  Award,
  Clock,
  FileText,
  BarChart3,
  UserCheck,
  Radar,
  TrendingUp,
  AlertTriangle,
  ArrowRight,
  Calendar,
  Play,
  Sparkles,
  Crown,
  Eye,
  CheckCircle2,
  Gift,
  Rocket,
  CalendarDays,
  LightbulbIcon,
  ChevronDown,
  Mail,
  MapPin,
  MessagesSquare
} from 'lucide-react';

// Animation helper - Intersection Observer Hook
function useInView(options = {}) {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
      }
    }, { threshold: 0.1, ...options });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return [ref, isInView];
}

// Animated Counter Component
function AnimatedCounter({ end, duration = 2000, suffix = '' }) {
  const [count, setCount] = useState(0);
  const [ref, isInView] = useInView();

  useEffect(() => {
    if (!isInView) return;
    
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [isInView, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

// Modules data
const modules = [
  {
    id: 'people',
    color: 'webtech-blue',
    gradient: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-500',
    title: 'HR People Mapping™',
    tagline: 'Votre entreprise, enfin visible.',
    description: 'Visualisez instantanément qui fait quoi, où, et sous quelle responsabilité.',
    whenToUse: 'Quand vous perdez du temps à chercher "qui reporte à qui"',
    features: [
      'Trombinoscope intelligent avec photos',
      'Organigramme vivant et interactif',
      'Fiches collaborateurs complètes',
      'Export PDF/PNG/CSV',
      'Multi-sites, multi-départements'
    ],
    result: 'Toute votre structure visible en 5 secondes.',
    icon: Users,
    free: true
  },
  {
    id: 'performance',
    color: 'webtech-yellow',
    gradient: 'from-amber-500 to-orange-500',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-500',
    title: 'HR Performance System™',
    tagline: 'L\'évaluation devient un système, pas un calvaire.',
    description: 'Campagnes d\'évaluation automatisées, objectifs alignés, feedback continu.',
    whenToUse: 'Quand vos évaluations annuelles prennent 3 mois au lieu de 3 semaines',
    features: [
      'Objectifs SMART alignés',
      'Évaluations 180° / 360°',
      'Feedback continu structuré',
      'Rapports PDF automatiques',
      'Scoring et ranking'
    ],
    result: 'Évaluations complètes en 1 semaine, pas 3 mois.',
    icon: Target,
    pro: true
  },
  {
    id: 'skills',
    color: 'purple-600',
    gradient: 'from-purple-500 to-indigo-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-500',
    title: 'HR Skills Intelligence™',
    tagline: 'Les compétences deviennent votre avantage stratégique.',
    description: 'Cartographie des compétences avec IA, détection des gaps, plans de développement.',
    whenToUse: 'Quand vous ne savez pas qui promouvoir, former ou remplacer',
    features: [
      'Matrice de compétences vivante',
      'Radar individuel & équipe',
      'Détection automatique des gaps',
      'Recommandations IA',
      'Plans de développement personnalisés'
    ],
    result: 'Décisions RH basées sur des données, pas des intuitions.',
    icon: Brain,
    pro: true,
    ai: true
  }
];

// Pain points
const painPoints = [
  { text: 'Fichiers Excel jamais à jour', icon: FileText },
  { text: 'Organigramme introuvable', icon: Network },
  { text: 'Évaluations qui n\'avancent jamais', icon: Calendar },
  { text: 'WhatsApp comme système de suivi', icon: MessagesSquare },
  { text: 'Zéro visibilité sur les compétences', icon: Eye }
];

// Timeline data
const timelineSteps = [
  {
    day: 'Jour 1',
    title: 'Visibilité RH immédiate',
    description: 'Organigramme et trombinoscope prêts. Tous vos employés dans un seul endroit.',
    icon: Eye,
    color: 'bg-blue-500'
  },
  {
    day: 'Jour 7',
    title: 'Premières analyses IA',
    description: 'Recommandations automatiques sur les risques RH et les opportunités.',
    icon: Brain,
    color: 'bg-purple-500'
  },
  {
    day: 'Jour 30',
    title: 'Décisions pilotées',
    description: 'Tableaux de bord, rapports DG, évaluations en cours. RH = moteur stratégique.',
    icon: Rocket,
    color: 'bg-green-500'
  }
];

// Credibility points
const credibilityPoints = [
  {
    icon: Globe,
    title: 'Conçu pour l\'Afrique',
    description: 'Hiérarchies complexes, multi-sites, réalités locales… Nous comprenons.'
  },
  {
    icon: Building2,
    title: 'Par Webtech Mali',
    description: 'Équipe IT locale, réactive, proche de vos besoins.'
  },
  {
    icon: Clock,
    title: 'Déployé en 48h',
    description: 'Pas d\'onboarding complexe. Opérationnel dès le jour 1.'
  }
];

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('fr-FR').format(amount) + ' F CFA';
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [userCount, setUserCount] = useState(10);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [freePlan, setFreePlan] = useState({ maxUsers: 3, maxEmployees: 10 });
  const [contactInfo, setContactInfo] = useState({
    email: 'contact@webtechmali.net',
    phone: '+223 76 00 00 00',
    address: 'Bamako, Mali'
  });

  // Fetch pricing and contact info from API
  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    
    const fetchPricing = async () => {
      try {
        const response = await fetch(`${API_URL}/api/billing/pricing`);
        if (response.ok) {
          const data = await response.json();
          if (data.tiers) {
            setTiers(data.tiers);
          }
          if (data.plans?.free) {
            setFreePlan(data.plans.free);
          }
        }
      } catch (error) {
        console.error('Error fetching pricing:', error);
      }
    };
    
    const fetchContactInfo = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/contact-info`);
        if (response.ok) {
          const data = await response.json();
          setContactInfo({
            email: data.email || 'contact@webtechmali.net',
            phone: data.phone || '+223 76 00 00 00',
            address: data.address || 'Bamako, Mali'
          });
        }
      } catch (error) {
        console.error('Error fetching contact info:', error);
      }
    };
    
    fetchPricing();
    fetchContactInfo();
  }, []);

  // Find current tier based on user count
  const getCurrentTier = () => {
    for (const tier of tiers) {
      if (tier.contactRequired) continue;
      const minUsers = tier.minUsers || 0;
      const maxUsers = tier.maxUsers;
      if (maxUsers === null || maxUsers === undefined) {
        if (userCount >= minUsers) return tier;
      } else if (minUsers <= userCount && userCount <= maxUsers) {
        return tier;
      }
    }
    return tiers.find(t => t.contactRequired) || null;
  };

  const currentTier = getCurrentTier();
  const annualPrice = currentTier?.priceYearly || 0;
  const isEnterprise = currentTier?.contactRequired || userCount > 1000;

  // Scroll to section
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-webtech-blue to-blue-700 rounded-xl flex items-center justify-center shadow-lg">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="font-heading font-bold text-xl text-slate-900">Webtech</span>
                <span className="font-heading font-bold text-xl text-webtech-blue ml-1">HR Engine™</span>
              </div>
            </Link>
            
            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('modules')} className="text-sm font-medium text-slate-600 hover:text-webtech-blue transition-colors">
                Modules
              </button>
              <button onClick={() => scrollToSection('pricing')} className="text-sm font-medium text-slate-600 hover:text-webtech-blue transition-colors">
                Tarifs
              </button>
              <button onClick={() => scrollToSection('why-us')} className="text-sm font-medium text-slate-600 hover:text-webtech-blue transition-colors">
                Pourquoi nous ?
              </button>
            </nav>

            <div className="flex items-center gap-3">
              <Link to="/login" className="hidden sm:block">
                <Button variant="ghost" className="text-slate-600 hover:text-webtech-blue">
                  Connexion
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-webtech-blue hover:bg-webtech-blue-hover text-white px-4 sm:px-6 shadow-lg shadow-webtech-blue/25 transition-all hover:shadow-xl hover:shadow-webtech-blue/30">
                  Créer mon compte
                </Button>
              </Link>
              
              {/* Mobile menu button */}
              <button 
                className="md:hidden p-2 hover:bg-slate-100 rounded-lg"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <ChevronDown className={`w-5 h-5 transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-100 animate-in slide-in-from-top duration-200">
              <nav className="flex flex-col gap-2">
                <button onClick={() => scrollToSection('modules')} className="text-left px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 rounded-lg">
                  Modules
                </button>
                <button onClick={() => scrollToSection('pricing')} className="text-left px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 rounded-lg">
                  Tarifs
                </button>
                <button onClick={() => scrollToSection('why-us')} className="text-left px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 rounded-lg">
                  Pourquoi nous ?
                </button>
                <Link to="/login" className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 rounded-lg">
                  Connexion
                </Link>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* ==================== HERO SECTION ==================== */}
      <section className="pt-24 sm:pt-28 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-white to-blue-50 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl from-webtech-blue/5 to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-webtech-yellow/10 rounded-full blur-3xl pointer-events-none" />
        
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              {/* Trust badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-webtech-yellow/20 to-amber-100 rounded-full border border-webtech-yellow/30 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <Award className="w-4 h-4 text-webtech-yellow" />
                <span className="text-sm font-semibold text-slate-800">🏆 Le Système RH #1 en Afrique de l'Ouest</span>
              </div>
              
              {/* Main headline */}
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100">
                Organisez, évaluez et développez vos équipes —{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-webtech-blue to-blue-600">sans Excel, sans chaos.</span>
              </h1>
              
              {/* Subheadline */}
              <p className="text-lg text-slate-600 max-w-2xl leading-relaxed animate-in fade-in slide-in-from-bottom-4 duration-500 delay-200">
                <span className="font-semibold text-webtech-blue">100% Gratuit pour commencer.</span> Pas de carte bancaire.
                <br /><br />
                Transformez votre RH en machine opérationnelle en moins de 24h. 
                Fini les fichiers Excel perdus, les organigrammes introuvables, les évaluations qui n'avancent pas.
              </p>
              
              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 pt-2 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-300">
                <Link to="/register">
                  <Button 
                    size="lg" 
                    className="bg-gradient-to-r from-webtech-blue to-blue-600 hover:from-webtech-blue-hover hover:to-blue-700 text-white px-8 py-6 text-lg rounded-xl shadow-xl shadow-webtech-blue/25 hover:shadow-2xl hover:shadow-webtech-blue/30 transition-all w-full sm:w-auto group"
                  >
                    <Gift className="w-5 h-5 mr-2" />
                    Créer mon compte gratuit
                    <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <a 
                  href="https://outlook.office.com/bookwithme/user/d1e6a15988744b1ca6e9289bc7d18a66@webtechmali.net/meetingtype/Lv4XqaONg021s1WVRkB42A2?anonymous&ep=mlink"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button 
                    variant="outline" 
                    size="lg"
                    className="border-2 border-slate-300 hover:border-webtech-blue hover:text-webtech-blue px-8 py-6 text-lg rounded-xl w-full sm:w-auto group"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Réserver une démo
                  </Button>
                </a>
              </div>
              
              {/* Trust signals */}
              <div className="flex flex-wrap items-center gap-6 pt-4 text-sm text-slate-500 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-400">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span>Déployé en 48h</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span>Sans carte bancaire</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span>Support local</span>
                </div>
              </div>
            </div>
            
            {/* Hero Visual */}
            <div className="lg:col-span-5 animate-in fade-in slide-in-from-right duration-700 delay-300">
              <div className="relative">
                {/* Main card */}
                <div className="bg-gradient-to-br from-webtech-blue to-blue-700 rounded-2xl overflow-hidden shadow-2xl p-6 sm:p-8">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-white hover:bg-white/20 transition-colors cursor-default">
                      <Users className="w-8 h-8 mb-2 opacity-90" />
                      <p className="text-2xl font-bold"><AnimatedCounter end={150} suffix="+" /></p>
                      <p className="text-sm opacity-80">Entreprises</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-white hover:bg-white/20 transition-colors cursor-default">
                      <BarChart3 className="w-8 h-8 mb-2 opacity-90" />
                      <p className="text-2xl font-bold"><AnimatedCounter end={10} suffix="K+" /></p>
                      <p className="text-sm opacity-80">Employés gérés</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-white hover:bg-white/20 transition-colors cursor-default">
                      <Star className="w-8 h-8 mb-2 opacity-90" />
                      <p className="text-2xl font-bold">4.9<span className="text-lg">/5</span></p>
                      <p className="text-sm opacity-80">Satisfaction</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-white hover:bg-white/20 transition-colors cursor-default">
                      <Clock className="w-8 h-8 mb-2 opacity-90" />
                      <p className="text-2xl font-bold">48<span className="text-lg">h</span></p>
                      <p className="text-sm opacity-80">Déploiement</p>
                    </div>
                  </div>
                </div>
                
                {/* Floating badge */}
                <div className="absolute -bottom-4 -left-4 bg-white p-4 rounded-xl shadow-xl border border-slate-100 animate-bounce-slow">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="w-5 h-5 text-green-600" />
                    </div>
                    <p className="text-sm font-medium text-slate-700">100% Gratuit pour démarrer</p>
                  </div>
                </div>
                
                {/* AI Badge */}
                <div className="absolute -top-4 -right-4 bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 animate-pulse">
                  <Brain className="w-4 h-4" />
                  <span className="text-sm font-semibold">IA Intégrée</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== SECTION "CE QUE VOUS GAGNEZ EN 24H" ==================== */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-green-100 text-green-700 mb-4">⚡ Résultats rapides</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Ce que vous gagnez en <span className="text-webtech-blue">24 heures</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Pas de formation complexe. Pas d'attente interminable. Résultats immédiats.
            </p>
          </div>
          
          {/* Timeline */}
          <div className="relative max-w-4xl mx-auto">
            {/* Connection line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 via-purple-500 to-green-500 hidden md:block" />
            
            <div className="space-y-12">
              {timelineSteps.map((step, idx) => {
                const [ref, isInView] = useInView();
                return (
                  <div 
                    key={idx}
                    ref={ref}
                    className={`
                      flex flex-col md:flex-row items-center gap-6 md:gap-12
                      ${idx % 2 === 1 ? 'md:flex-row-reverse' : ''}
                      transition-all duration-700
                      ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
                    `}
                  >
                    {/* Content */}
                    <div className={`flex-1 ${idx % 2 === 1 ? 'md:text-right' : ''}`}>
                      <Badge className={`${step.color} text-white mb-3`}>{step.day}</Badge>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">{step.title}</h3>
                      <p className="text-slate-600">{step.description}</p>
                    </div>
                    
                    {/* Icon */}
                    <div className={`w-16 h-16 ${step.color} rounded-2xl flex items-center justify-center shadow-lg z-10`}>
                      <step.icon className="w-8 h-8 text-white" />
                    </div>
                    
                    {/* Spacer for alignment */}
                    <div className="flex-1 hidden md:block" />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ==================== SECTION PROBLÈMES RH ==================== */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }} />
        
        <div className="max-w-4xl mx-auto text-center relative">
          <AlertTriangle className="w-12 h-12 text-webtech-yellow mx-auto mb-6" />
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
            Ce que vous faites aujourd'hui<br /><span className="text-red-400">ne marche pas.</span>
          </h2>
          <p className="text-lg text-slate-300 mb-10">
            Soyons honnêtes : Si votre RH ressemble à ça…
          </p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
            {painPoints.map((point, idx) => (
              <div 
                key={idx} 
                className="flex items-center gap-3 bg-slate-800/80 backdrop-blur rounded-lg p-4 hover:bg-slate-800 transition-colors group"
              >
                <X className="w-5 h-5 text-red-400 flex-shrink-0 group-hover:scale-110 transition-transform" />
                <span className="text-slate-200 text-sm text-left">{point.text}</span>
              </div>
            ))}
          </div>
          
          {/* Mirror phrase */}
          <div className="bg-slate-800/50 backdrop-blur rounded-xl p-6 mb-8 border border-slate-700">
            <p className="text-xl text-slate-300 italic">
              "Ce n'est pas votre faute. <span className="text-white font-semibold">C'est l'outil qui est mauvais.</span>"
            </p>
          </div>
          
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-webtech-yellow/30 to-amber-500/20 text-webtech-yellow px-6 py-3 rounded-full font-semibold border border-webtech-yellow/30">
            <Check className="w-5 h-5" />
            Webtech HR Engine™ remplace tout ça, immédiatement.
          </div>
        </div>
      </section>

      {/* ==================== SECTION MODULES ==================== */}
      <section id="modules" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-webtech-blue/10 text-webtech-blue mb-4">🔥 Tout-en-un</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Les 3 modules qui remplacent <span className="text-webtech-blue">7 outils</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Un seul système pour tout gérer. Simple. Puissant. Africain.
            </p>
          </div>
          
          <div className="space-y-8">
            {modules.map((module, idx) => {
              const [ref, isInView] = useInView();
              const isEven = idx % 2 === 0;
              
              return (
                <div
                  key={module.id}
                  ref={ref}
                  className={`
                    flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center
                    transition-all duration-700
                    ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}
                  `}
                >
                  {/* Visual */}
                  <div className="lg:w-1/2">
                    <div className={`
                      bg-gradient-to-br ${module.gradient} rounded-2xl p-8 shadow-2xl
                      transform hover:scale-[1.02] transition-transform duration-300
                    `}>
                      <div className="bg-white/10 backdrop-blur rounded-xl p-6 text-white">
                        <module.icon className="w-12 h-12 mb-4" />
                        <h3 className="text-2xl font-bold mb-2">{module.title}</h3>
                        <p className="opacity-90">{module.tagline}</p>
                        
                        {/* Badges */}
                        <div className="flex gap-2 mt-4">
                          {module.free && (
                            <Badge className="bg-white/20 text-white">GRATUIT</Badge>
                          )}
                          {module.pro && (
                            <Badge className="bg-webtech-yellow text-slate-900">BUSINESS</Badge>
                          )}
                          {module.ai && (
                            <Badge className="bg-purple-200 text-purple-800">
                              <Brain className="w-3 h-3 mr-1" />
                              IA
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="lg:w-1/2 space-y-4">
                    <p className="text-lg text-slate-600">{module.description}</p>
                    
                    {/* When to use */}
                    <div className={`${module.bgColor} rounded-lg p-4 border-l-4 ${module.borderColor}`}>
                      <p className="text-sm font-medium text-slate-700">
                        <span className="font-bold">📍 Quand l'utiliser ?</span><br />
                        {module.whenToUse}
                      </p>
                    </div>
                    
                    {/* Features */}
                    <ul className="space-y-2">
                      {module.features.map((feature, fidx) => (
                        <li key={fidx} className="flex items-start gap-2 text-slate-600">
                          <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    {/* Result */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                      <p className="text-sm font-semibold text-green-800">
                        ✨ Résultat : {module.result}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==================== SECTION PRICING ==================== */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-webtech-yellow/20 text-slate-800 mb-4">💰 Tarification simple</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Le pricing <span className="text-webtech-blue">simple & transparent</span>
            </h2>
            <p className="text-lg text-slate-600">
              Pas de frais cachés. Pas de surprise. Tarification annuelle en F CFA.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* FREE PLAN */}
            <Card className="border-2 border-slate-200 hover:border-slate-300 transition-all hover:shadow-lg group">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Gift className="w-6 h-6 text-slate-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-xl text-slate-900">STARTER</h3>
                    <p className="text-sm text-slate-500">Pour Startups & PME en croissance</p>
                  </div>
                </div>
                
                <div className="mb-6">
                  <span className="text-5xl font-bold text-slate-900">0</span>
                  <span className="text-xl font-bold text-slate-900 ml-1">F CFA</span>
                  <span className="text-slate-500 ml-2">pour toujours</span>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {[
                    `Jusqu'à ${freePlan.maxUsers} utilisateur${freePlan.maxUsers > 1 ? 's' : ''}`,
                    '1 module : HR People Mapping™',
                    'Trombinoscope complet',
                    'Organigramme basique',
                    'Sans carte bancaire'
                  ].map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-slate-600">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <p className="text-sm text-slate-500 mb-6 italic">
                  Parfait pour découvrir et commencer.
                </p>
                
                <Link to="/register?plan=free">
                  <Button variant="outline" className="w-full py-6 text-lg border-2 group-hover:border-webtech-blue group-hover:text-webtech-blue transition-colors">
                    Commencer gratuitement
                  </Button>
                </Link>
              </CardContent>
            </Card>
            
            {/* PRO PLAN */}
            <Card className="border-2 border-webtech-blue shadow-xl relative overflow-hidden transform hover:scale-[1.02] transition-all">
              <div className="absolute top-0 right-0 bg-gradient-to-r from-webtech-yellow to-amber-500 text-slate-900 text-xs font-bold px-4 py-1.5 rounded-bl-xl">
                <Crown className="w-3 h-3 inline mr-1" />
                RECOMMANDÉ
              </div>
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-webtech-blue to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                    <Crown className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-xl text-slate-900">BUSINESS</h3>
                    <p className="text-sm text-slate-500">Pour entreprises, groupes et organisations</p>
                  </div>
                </div>
                
                {/* User count selector */}
                <div className="mb-4 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-600">Nombre d'utilisateurs</span>
                    <span className="font-bold text-webtech-blue">{userCount}</span>
                  </div>
                  <input
                    type="range"
                    min={10}
                    max={1100}
                    step={10}
                    value={userCount}
                    onChange={(e) => setUserCount(parseInt(e.target.value))}
                    className="w-full accent-webtech-blue"
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>10</span>
                    <span>100</span>
                    <span>500</span>
                    <span>1000+</span>
                  </div>
                </div>
                
                {/* Current tier info */}
                {currentTier && !isEnterprise && (
                  <div className="mb-4 text-center">
                    <Badge className="bg-webtech-blue/10 text-webtech-blue">
                      {currentTier.name}
                    </Badge>
                  </div>
                )}
                
                {/* Price display or Enterprise CTA */}
                {isEnterprise ? (
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-6 mb-6 border border-slate-200 text-center">
                    <Building2 className="w-12 h-12 text-webtech-blue mx-auto mb-3" />
                    <h4 className="font-bold text-lg text-slate-900 mb-2">Offre sur mesure</h4>
                    <p className="text-sm text-slate-600 mb-4">
                      Pour plus de 1000 utilisateurs, contactez-nous pour un devis personnalisé.
                    </p>
                    <Link to="/contact">
                      <Button className="w-full bg-gradient-to-r from-webtech-blue to-blue-600">
                        <Phone className="w-4 h-4 mr-2" />
                        Contactez-nous
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <>
                    <div className="bg-webtech-blue/5 rounded-lg p-4 mb-6 border border-webtech-blue/20">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-slate-600">Prix annuel</span>
                        <span className="font-bold text-2xl text-webtech-blue">{formatCurrency(annualPrice)}</span>
                      </div>
                      <p className="text-xs text-slate-500 text-right">pour {userCount} utilisateurs / an</p>
                    </div>
                    
                    <ul className="space-y-3 mb-6">
                      {[
                        `Jusqu'à ${currentTier?.maxUsers || userCount} utilisateurs`,
                        'Tous les modules débloqués',
                        'Analyse IA & recommandations',
                        'Évaluations 180° / 360°',
                        'Rapports PDF automatiques',
                        'Support prioritaire'
                      ].map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-3 text-slate-600">
                          <Check className="w-5 h-5 text-webtech-blue flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    <p className="text-xs text-slate-500 mb-4 flex items-center gap-1">
                      <Shield className="w-4 h-4" />
                      Résiliable à tout moment. Satisfait ou remboursé 14 jours.
                    </p>
                    
                    <Link to="/register?plan=pro">
                      <Button className="w-full py-6 text-lg bg-gradient-to-r from-webtech-blue to-blue-600 hover:from-webtech-blue-hover hover:to-blue-700 shadow-lg shadow-webtech-blue/25">
                        Passer à BUSINESS
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </Link>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ==================== SECTION CRÉDIBILITÉ ==================== */}
      <section id="why-us" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Pourquoi <span className="text-webtech-blue">nous faire confiance</span> ?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {credibilityPoints.map((point, idx) => {
              const [ref, isInView] = useInView();
              return (
                <div 
                  key={idx}
                  ref={ref}
                  className={`
                    bg-white p-8 rounded-xl shadow-sm border border-slate-200 text-center
                    hover:shadow-xl hover:border-webtech-blue/30 transition-all duration-300
                    ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
                  `}
                  style={{ transitionDelay: `${idx * 100}ms` }}
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-webtech-blue to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <point.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-slate-900 mb-3">
                    {point.title}
                  </h3>
                  <p className="text-slate-600">{point.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==================== SECTION GARANTIE ==================== */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-webtech-blue to-blue-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }} />
        
        <div className="max-w-4xl mx-auto text-center relative">
          <Shield className="w-16 h-16 mx-auto mb-6 opacity-90" />
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4">
            🔐 Garantie Satisfaction 14 Jours
          </h2>
          <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
            Si vous n'êtes pas satisfait après 14 jours d'utilisation BUSINESS, 
            nous vous remboursons intégralement. Sans question.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Badge variant="secondary" className="bg-white/20 text-white px-4 py-2">
              ✓ Aucun risque
            </Badge>
            <Badge variant="secondary" className="bg-white/20 text-white px-4 py-2">
              ✓ Résiliable à tout moment
            </Badge>
            <Badge variant="secondary" className="bg-white/20 text-white px-4 py-2">
              ✓ Support inclus
            </Badge>
          </div>
        </div>
      </section>

      {/* ==================== CTA FINAL ==================== */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            🚀 Prêt à transformer votre RH ?
          </h2>
          <p className="text-xl text-slate-600 mb-10">
            Rejoignez les <span className="font-semibold text-webtech-blue">150+ entreprises</span> qui font confiance à Webtech HR Engine™
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-webtech-blue to-blue-600 hover:from-webtech-blue-hover hover:to-blue-700 text-white px-10 py-6 text-lg rounded-xl shadow-xl shadow-webtech-blue/25 group"
              >
                <Gift className="w-5 h-5 mr-2" />
                Créer mon compte gratuit
                <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <a 
              href="https://outlook.office.com/bookwithme/user/d1e6a15988744b1ca6e9289bc7d18a66@webtechmali.net/meetingtype/Lv4XqaONg021s1WVRkB42A2?anonymous&ep=mlink"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button 
                variant="outline" 
                size="lg"
                className="border-2 border-slate-300 px-10 py-6 text-lg rounded-xl hover:border-webtech-blue hover:text-webtech-blue"
              >
                <Phone className="w-5 h-5 mr-2" />
                Réserver une démo
              </Button>
            </a>
          </div>
          
          <p className="text-sm text-slate-500 mt-6 flex items-center justify-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            Aucune carte bancaire requise
          </p>
        </div>
      </section>

      {/* ==================== FOOTER ==================== */}
      <footer className="bg-slate-900 text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-webtech-blue to-blue-600 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <span className="font-heading font-bold text-xl">HR Engine™</span>
              </div>
              <p className="text-slate-400 text-sm mb-4">
                Le système RH le plus simple d'Afrique.
              </p>
              <div className="space-y-2 text-sm text-slate-400">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <a href={`mailto:${contactInfo.email}`} className="hover:text-white transition-colors">
                    {contactInfo.email}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="hover:text-white transition-colors">
                    {contactInfo.phone}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>{contactInfo.address}</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Produit</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><button onClick={() => scrollToSection('modules')} className="hover:text-white transition-colors">Modules</button></li>
                <li><button onClick={() => scrollToSection('pricing')} className="hover:text-white transition-colors">Tarifs</button></li>
                <li><Link to="/register" className="hover:text-white transition-colors">Commencer</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Entreprise</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><button onClick={() => scrollToSection('why-us')} className="hover:text-white transition-colors">À propos</button></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Légal</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#" className="hover:text-white transition-colors">CGU</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Confidentialité</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Cookies</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-400">
              © {new Date().getFullYear()} Webtech Mali. Tous droits réservés.
            </p>
            <div className="flex items-center gap-4">
              <a href={`mailto:${contactInfo.email}`} className="text-slate-400 hover:text-white transition-colors" title={contactInfo.email}>
                <Mail className="w-5 h-5" />
              </a>
              <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="text-slate-400 hover:text-white transition-colors" title={contactInfo.phone}>
                <Phone className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* CSS Animation keyframes */}
      <style>{`
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
